"use client";

import React, { useEffect, useState } from "react";
import { Users } from "lucide-react";
import { motion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
function FacultyGridConsumer({
  lang,
  containerVariants,
  itemVariants
}: {
  lang: "vi" | "en";
  containerVariants: any;
  itemVariants: any;
}) {
  const {
    posts,
    loading,
    error
  } = usePosts();
  if (loading) {
    return <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {[...Array(6)].map((_, i) => <div key={i} className="h-80 rounded-xl bg-muted/40 animate-pulse border border-border" />)}
      </div>;
  }
  if (error || !posts || posts.length === 0) {
    return <div className="text-center py-12 text-muted-foreground text-sm">
        {lang === "vi" ? "Chưa có thông tin giảng viên." : "No faculty profiles found."}
      </div>;
  }
  return <motion.div initial="hidden" whileInView="visible" viewport={{
    once: true,
    margin: "-50px"
  }} variants={containerVariants} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {posts.map((post: any, i: number) => {
      const photoSrc = typeof post.acf?.photo === "string" ? post.acf.photo : post.acf?.photo?.url || "";
      const image = {
        src: photoSrc,
        srcset: "",
        sizes: ""
      };
      return <motion.div key={post.id || i} variants={itemVariants} data-index={i} className="h-full">
            <Card className="h-full bg-card border-border border transition-[border-color,box-shadow,transform] duration-200 hover:-translate-y-1 hover:border-secondary hover:shadow-md flex flex-col justify-between rounded-xl p-5">
              <div>
                <div className="relative aspect-4/3 w-full overflow-hidden rounded-lg bg-muted border border-border/60">
                  {image.src ? <img src={image.src} data-wvc-srcset={image.srcset} data-wvc-sizes={image.sizes} alt={post.acf?.full_name || "Faculty Member"} loading="lazy" className="h-full w-full object-cover object-center" /> : <div className="h-full w-full flex items-center justify-center bg-muted text-muted-foreground">
                      <Users className="size-10 opacity-40" aria-hidden="true" />
                    </div>}
                  <div className="absolute top-2.5 right-2.5">
                    <Badge variant="secondary" className="text-[10px] font-medium tracking-tight bg-background/90 text-foreground backdrop-blur-xs border-border/80 rounded-md py-0.5 px-2 shadow-xs">
                      {lang === "vi" ? "Hồ sơ minh họa" : "Illustrative Profile"}
                    </Badge>
                  </div>
                </div>

                <CardHeader className="p-0 pt-4 pb-1">
                  <CardTitle className="text-lg font-bold text-card-foreground">
                    {post.acf?.full_name}
                  </CardTitle>
                  {post.acf?.role_title && <p className="text-xs font-semibold text-primary mt-1">
                      {post.acf.role_title}
                    </p>}
                </CardHeader>

                <CardContent className="p-0 pt-2">
                  {post.acf?.specialty && <div className="mt-1">
                      <span className="text-[11px] uppercase tracking-wider font-semibold text-muted-foreground block mb-0.5">
                        {lang === "vi" ? "Chuyên môn" : "Specialty"}
                      </span>
                      <p className="text-xs text-muted-foreground leading-relaxed line-clamp-2">
                        {post.acf.specialty}
                      </p>
                    </div>}
                </CardContent>
              </div>

              {post.link && <div className="mt-4 pt-3 border-t border-border/60">
                  <Link to={post.link} className="text-xs font-semibold text-primary hover:text-primary/80 inline-flex items-center gap-1">
                    <span>{lang === "vi" ? "Xem chi tiết" : "View profile"}</span>
                    <span aria-hidden="true">&rarr;</span>
                  </Link>
                </div>}
            </Card>
          </motion.div>;
    })}
    </motion.div>;
}
export default function FacultyTeaser() {
  const [lang, setLang] = useState<"vi" | "en">("vi");
  useEffect(() => {
    const readStoredLang = () => {
      try {
        const stored = localStorage.getItem("roboto_lang");
        if (stored === "en" || stored === "vi") {
          setLang(stored);
        }
      } catch {
        // Local storage inaccessible
      }
    };
    readStoredLang();
    const handleCustomChange = () => {
      readStoredLang();
    };
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === "roboto_lang") {
        readStoredLang();
      }
    };
    window.addEventListener("roboto-lang-change", handleCustomChange);
    window.addEventListener("storage", handleStorageChange);
    return () => {
      window.removeEventListener("roboto-lang-change", handleCustomChange);
      window.removeEventListener("storage", handleStorageChange);
    };
  }, []);
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.06
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.35,
        ease: "easeOut" as const
      }
    }
  };
  return <section id="facultyteaser" data-nav="light" className="relative bg-background text-foreground py-20 lg:py-28 overflow-hidden border-b border-border">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: "-80px"
      }} variants={containerVariants} className="flex flex-col items-center text-center max-w-3xl mx-auto mb-14 lg:mb-16">
          {/* Ratio badge */}
          <motion.div variants={itemVariants} className="mb-4">
            <Badge variant="outline" className="px-3.5 py-1.5 text-xs font-semibold tracking-wide border-secondary/50 bg-secondary/10 text-secondary-foreground gap-2 rounded-full shadow-2xs">
              <Users className="size-3.5 text-secondary" aria-hidden="true" />
              <span>
                {lang === "vi" ? "Tỷ lệ chuẩn 1 giảng viên : 6 học viên" : "1 mentor : 6 students standard ratio"}
              </span>
            </Badge>
          </motion.div>

          {/* Heading */}
          <motion.h2 variants={itemVariants} className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-foreground font-serif">
            {lang === "vi" ? "Đội ngũ giảng viên ROBOTO" : "Meet the ROBOTO Faculty"}
          </motion.h2>

          {/* Subtitle clarifying specialty tracks */}
          <motion.p variants={itemVariants} className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
            {lang === "vi" ? "Hệ thống chuyên môn giảng dạy tiêu chuẩn học viện dành cho độ tuổi 4–16, tổ chức theo 6 phân môn cốt lõi thay vì phân bổ hồ sơ cá nhân đơn lẻ." : "Structured academy teaching specialties for ages 4–16, organized across 6 technical domains rather than individual instructor profiles."}
          </motion.p>
        </motion.div>

        {/* Dynamic Teacher Posts Grid */}
        <PostsProvider wp_query={{
        post_type: "teacher",
        per_page: 6,
        status: ["publish"]
      }}>
          <FacultyGridConsumer lang={lang} containerVariants={containerVariants} itemVariants={itemVariants} />
        </PostsProvider>

        {/* Action Button */}
        <motion.div initial={{
        opacity: 0,
        y: 16
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.35,
        delay: 0.2
      }} className="mt-12 lg:mt-16 text-center">
          <Button asChild size="lg" className="rounded-lg shadow-sm px-8">
            <Link to="/TeachersPage">
              {lang === "vi" ? "Xem đội ngũ đầy đủ" : "View Full Faculty"}
            </Link>
          </Button>
        </motion.div>
      </div>
    </section>;
}